import * as React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  onSend: (text: string) => void;
  isLimitReached: boolean;
  // Text submitted while the previous answer was still streaming.
  pendingMessage?: string | null;
  onStartNewChat?: () => void;
};

const AiChatInput: React.FC<React.PropsWithChildren<Props>> = ({
  onSend,
  isLimitReached,
  pendingMessage,
  onStartNewChat,
}) => {
  const { t } = useTranslation();
  const [text, setText] = React.useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  React.useLayoutEffect(() => {
    adjustHeight();
  }, [text]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isLimitReached) return;
    onSend(trimmed);
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Only the message limit blocks typing. Disabling the field while an answer
  // streamed meant everything the employee typed was thrown away with no
  // notice (FLA-8235) — a mid-stream message is queued instead.
  const isDisabled = isLimitReached;
  const canSend = text.trim().length > 0 && !isDisabled;

  return (
    <div>
      {isLimitReached && (
        <div className="ai-chat-limit-warning">
          <span className="ai-chat-limit-warning-text">
            {t('aiChat.input.limitWarning')}
          </span>
          {onStartNewChat && (
            <button
              type="button"
              className="ai-chat-limit-warning-action"
              onClick={onStartNewChat}>
              {t('aiChat.input.startNewChat')}
            </button>
          )}
        </div>
      )}
      {pendingMessage && (
        <div className="ai-chat-queued-note" role="status">
          {t('aiChat.input.queued')}
        </div>
      )}
      <div
        className={`ai-chat-input-wrapper${isDisabled ? ' disabled' : ''}`}
        onClick={() => !isDisabled && textareaRef.current?.focus()}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('aiChat.input.placeholder')}
          disabled={isDisabled}
          rows={1}
          className="ai-chat-input-textarea"
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`ai-chat-send-btn ${canSend ? 'active' : 'inactive'}`}>
          ↑
        </button>
      </div>
    </div>
  );
};

export default AiChatInput;
